import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, KanbanSquare, Calendar as CalendarIcon, ListTodo,
  Settings as SettingsIcon, LogOut, Inbox, Ticket as TicketIcon,
  TrendingUp, Repeat, AlertCircle, Trophy, BookOpen, Lightbulb, Menu, X,
} from "lucide-react";
import Logo from "./Logo";
import { useAuth } from "../lib/auth";

const adminItems = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "sidebar-nav-dashboard", exact: true },
  { to: "/work-orders", label: "Clients", icon: Inbox, testId: "sidebar-nav-clients" },
  { to: "/projects", label: "Projects", icon: KanbanSquare, testId: "sidebar-nav-projects" },
  { to: "/lead-pipeline", label: "Lead Pipeline", icon: TrendingUp, testId: "sidebar-nav-lead-pipeline" },
  { to: "/subscriptions", label: "Subscriptions", icon: Repeat, testId: "sidebar-nav-subscriptions" },
  { to: "/deliverables", label: "Deliverables", icon: AlertCircle, testId: "sidebar-nav-deliverables" },
  { to: "/successes", label: "Monthly Successes", icon: Trophy, testId: "sidebar-nav-successes" },
  { to: "/calendar", label: "Calendar", icon: CalendarIcon, testId: "sidebar-nav-calendar" },
  { to: "/todos", label: "Daily To-Do", icon: ListTodo, testId: "sidebar-nav-todos" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testId: "sidebar-nav-settings" },
];

const clientItems = [
  { to: "/tickets", label: "Work Tickets", icon: TicketIcon, testId: "sidebar-nav-tickets", exact: true },
  { to: "/inspiration", label: "Inspiration Board", icon: Lightbulb, testId: "sidebar-nav-inspiration" },
];

export default function Sidebar({ mobileOpen, onCloseMobile }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const role = user?.role;
  const isAdmin = role === "admin" || role === "viewer";
  const isViewer = role === "viewer";
  const isReadmeOnly = role === "readme_author";

  const showReadMe = isAdmin || isReadmeOnly;
  const items = isReadmeOnly ? [] : isAdmin ? adminItems : clientItems;

  // Close mobile drawer whenever route changes
  React.useEffect(() => { onCloseMobile && onCloseMobile(); /* eslint-disable-next-line */ }, [location.pathname]);

  const content = (
    <>
      <div className="mb-8 flex items-center justify-between px-1">
        <Logo size="md" />
        <button
          data-testid="sidebar-mobile-close"
          onClick={onCloseMobile}
          className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-100 md:hidden"
          aria-label="Close menu"
        >
          <X size={20} />
        </button>
      </div>

      <nav className="flex flex-col gap-1">
        {items.map(({ to, label, icon: Icon, testId, exact }) => (
          <NavLink
            key={to}
            to={to}
            end={exact}
            data-testid={testId}
            className={({ isActive }) =>
              `flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? "bg-[#E5F0FF] text-[#0066ff] font-semibold"
                  : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
              }`
            }
          >
            <Icon size={18} strokeWidth={1.6} />
            <span>{label}</span>
          </NavLink>
        ))}
        {showReadMe && (
          <NavLink
            to="/read-me"
            data-testid="sidebar-nav-readme"
            className={({ isActive }) =>
              `flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                isActive ? "bg-[#E5F0FF] text-[#0066ff]" : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
              }`
            }
          >
            <BookOpen size={18} strokeWidth={1.6} />
            <span>Read Me</span>
          </NavLink>
        )}
      </nav>

      <div className="mt-auto flex flex-col gap-3 border-t border-zinc-200 pt-4">
        {user && (
          <div className="flex items-center gap-3 px-1">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#E5F0FF] text-sm font-semibold text-[#0066ff]">
              {(user.name || user.email || "U").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <div className="truncate text-sm font-medium text-zinc-900">{user.name || "User"}</div>
                {isViewer && (
                  <span data-testid="viewer-badge" className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-amber-800">
                    View-only
                  </span>
                )}
                {isReadmeOnly && (
                  <span data-testid="readme-author-badge" className="rounded-full bg-[#E5F0FF] px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-[#0066ff]">
                    Author
                  </span>
                )}
              </div>
              <div className="truncate text-xs text-zinc-500">{user.email}</div>
            </div>
          </div>
        )}
        <button
          data-testid="sidebar-logout-button"
          onClick={logout}
          className="flex w-full items-center justify-start gap-3 rounded-md px-3 py-2 text-sm font-medium text-zinc-600 transition-colors hover:bg-zinc-100"
        >
          <LogOut size={18} strokeWidth={1.6} />
          <span>Log out</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        data-testid="app-sidebar"
        className="fixed left-0 top-0 z-30 hidden h-screen w-[260px] flex-col border-r border-zinc-200 bg-white p-5 md:flex"
      >
        {content}
      </aside>

      {/* Mobile drawer overlay + drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden" data-testid="sidebar-mobile-overlay">
          <div className="absolute inset-0 bg-black/40" onClick={onCloseMobile} />
          <aside
            data-testid="app-sidebar-mobile"
            className="absolute left-0 top-0 flex h-full w-[280px] max-w-[85%] flex-col overflow-y-auto border-r border-zinc-200 bg-white p-5 shadow-2xl"
          >
            {content}
          </aside>
        </div>
      )}
    </>
  );
}

export function MobileTopBar({ onOpenMenu }) {
  return (
    <div
      data-testid="mobile-topbar"
      className="sticky top-0 z-20 flex items-center justify-between border-b border-zinc-200 bg-white/95 px-4 py-3 backdrop-blur md:hidden"
    >
      <button
        data-testid="mobile-menu-button"
        onClick={onOpenMenu}
        className="rounded-md p-1.5 text-zinc-600 hover:bg-zinc-100"
        aria-label="Open menu"
      >
        <Menu size={20} />
      </button>
      <Logo size="sm" />
      <div className="w-8" />
    </div>
  );
}
